import type {
  ReassignmentDetailsResponse,
  ReassignmentPrefillResponse,
  ReassignmentReasonResponse,
} from "@credit-acceptance/step6-bff-client";
import { toast } from "@credit-acceptance/uikit";
import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Textarea } from "../../../../components/ui/textarea";
import { getErrorMessage, getProblemDetail, isHttpError } from "../../../../lib/query/errors";
import { openMailContract } from "../../../actions/api/openMailContract";
import { useReassignment, useReassignmentPrefill, useReassignmentReasons } from "./api/useReassignment";
import { useSaveReassignment } from "./api/useSaveReassignment";

export type ReassignmentStatusCode = "RAR" | "RAP" | "RAC";

interface ReassignmentModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
  /** Current deal status. "Reassignment Completed" is read-only once the deal is already RAC. */
  statusCode: ReassignmentStatusCode;
  /** Plan type shown read-only (the deal's program type). */
  planType?: string;
  /** Caller's department role name, forwarded to the save endpoint. */
  roleName?: string;
  /** Runs after a successful save, once the popup has closed (the Exit flow releases the lock and navigates here). */
  onSaved?: () => void | Promise<void>;
  /** Runs when the analyst cancels or dismisses the popup without saving. */
  onCancel?: () => void;
}

interface FormState {
  dealerContactName: string;
  telephoneNumber: string;
  faxNumber: string;
  reasonId: string;
  approvedByOwnerOrGm: string;
  reAssignmentComplete: string;
  lienReleaseRequired: string;
  notes: string;
}

type FieldKey = keyof FormState;

const YES_NO = ["Y", "N"] as const;
const labelClassName = "text-(length:--typography-base-sizes-extra-small-font-size)";
const fieldClassName = "gap-(--spacing-1) flex flex-col";
const noteDateFormat = new Intl.DateTimeFormat("en-US", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "America/New_York",
});

const formatNoteDate = (value: string | undefined) => {
  const date = value ? new Date(value) : undefined;
  return date && !Number.isNaN(date.getTime()) ? noteDateFormat.format(date) : (value ?? "");
};

const PHONE_PATTERN = /^\d{3}-\d{3}-\d{4}$/;

// Stored phone/fax values are raw (may be null, unformatted, or carry a leading country code). Formats
// 10 digits (or 11 with a leading 1) to ###-###-####; anything else is left as-is so validation flags it.
export const formatPhone = (value: string | undefined | null): string => {
  const raw = value?.trim() ?? "";
  const digits = raw.replaceAll(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return national.length === 10 ? `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}` : raw;
};

const toYesNo = (value: string | undefined) =>
  value?.toUpperCase() === "Y" ? "Y" : value?.toUpperCase() === "N" ? "N" : "";

interface ReassignmentFormProperties {
  approvalId: string;
  statusCode: ReassignmentStatusCode;
  planType?: string;
  roleName?: string;
  details: ReassignmentDetailsResponse;
  reasons: ReassignmentReasonResponse[];
  prefill?: ReassignmentPrefillResponse;
  autopay: string;
  onSaved: () => void | Promise<void>;
}

// Mounted only once the saved record and reasons have resolved, so state seeds straight from the
// response. The dialog unmounts its content on close, so reopening re-seeds from the refetch.
function ReassignmentForm({
  approvalId,
  statusCode,
  planType,
  roleName,
  details,
  reasons,
  prefill,
  autopay,
  onSaved,
}: ReassignmentFormProperties) {
  const { t } = useTranslation();
  const saveReassignment = useSaveReassignment(approvalId, roleName);

  const savedReason = details.reasonForReAssignment;
  // The reasons list is active-only; a saved reason missing from it is inactive now. It is still shown
  // (as a disabled option) but the user must pick a different one before saving.
  const savedReasonInactive = savedReason?.id !== undefined && !reasons.some((reason) => reason.id === savedReason.id);

  const [form, setForm] = useState<FormState>({
    dealerContactName: details.dealerContactName ?? "",
    telephoneNumber: formatPhone(details.telephoneNumber || prefill?.telephoneNumber),
    faxNumber: formatPhone(details.faxNumber || prefill?.faxNumber),
    reasonId: savedReason?.id === undefined ? "" : String(savedReason.id),
    approvedByOwnerOrGm: toYesNo(details.approvedByOwnerOrGm),
    reAssignmentComplete: toYesNo(details.reAssignmentComplete),
    lienReleaseRequired: toYesNo(details.lienReleaseRequired),
    notes: "",
  });
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [submitError, setSubmitError] = useState<string>();
  const [openingContract, setOpeningContract] = useState(false);

  const completedReadOnly = statusCode === "RAC";

  const setField = (key: FieldKey, value: string) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  const validate = (): boolean => {
    const required = t("dealStatus.reassignmentModal.required");
    const next: Partial<Record<FieldKey, string>> = {};
    for (const key of [
      "dealerContactName",
      "telephoneNumber",
      "faxNumber",
      "approvedByOwnerOrGm",
      "reAssignmentComplete",
      "lienReleaseRequired",
    ] as const) {
      if (!form[key].trim()) {
        next[key] = required;
      }
    }
    for (const key of ["telephoneNumber", "faxNumber"] as const) {
      if (form[key].trim() && !PHONE_PATTERN.test(form[key].trim())) {
        next[key] = t("dealStatus.reassignmentModal.phoneFormat");
      }
    }
    if (!form.reasonId) {
      next.reasonId = required;
    } else if (savedReasonInactive && form.reasonId === String(savedReason?.id)) {
      next.reasonId = t("dealStatus.reassignmentModal.reasonInactive");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    setSubmitError(undefined);
    if (!validate()) {
      return;
    }
    const reason = reasons.find((item) => String(item.id) === form.reasonId);
    try {
      // `expectedVersion` is intentionally omitted: it is optional and CAS exposes no status version.
      await saveReassignment.mutateAsync({
        dealerContactName: form.dealerContactName.trim(),
        telephoneNumber: form.telephoneNumber.trim(),
        faxNumber: form.faxNumber.trim(),
        reasonForReAssignment: { id: reason?.id },
        lienReleaseRequired: form.lienReleaseRequired,
        approvedByOwnerOrGm: form.approvedByOwnerOrGm,
        reAssignmentComplete: form.reAssignmentComplete,
        notes: form.notes.trim() || undefined,
      });
    } catch (error) {
      // Placeholder: CAS doesn't enforce a version yet, so a 409 can't fire today.
      if (isHttpError(error, 409)) {
        setSubmitError(t("dealStatus.reassignmentModal.conflict"));
      } else if (isHttpError(error, 403) || isHttpError(error, 422)) {
        // role_required / REASSIGNMENT_NOT_ALLOWED carry a user-facing message from the backend.
        const problem = getProblemDetail(error);
        setSubmitError(problem?.detail || problem?.title || t("dealStatus.reassignmentModal.notAllowed"));
      } else {
        setSubmitError(t("dealStatus.reassignmentModal.saveError", { message: getErrorMessage(error) }));
      }
      return;
    }
    toast.success(t("dealStatus.reassignmentModal.saveSuccess"));
    await onSaved();
  };

  const handleMailContract = async () => {
    setOpeningContract(true);
    try {
      await openMailContract(approvalId, "reassignment");
    } catch {
      toast.error(t("dealStatus.reassignmentModal.mailContractError"));
    } finally {
      setOpeningContract(false);
    }
  };

  const yesNoField = (
    key: "approvedByOwnerOrGm" | "reAssignmentComplete" | "lienReleaseRequired",
    label: string,
    disabled = false
  ) => (
    <div className={fieldClassName}>
      <Label htmlFor={`reassignment-${key}`} className={labelClassName}>
        {label}
      </Label>
      <Select value={form[key]} onValueChange={(value) => setField(key, value)} disabled={disabled}>
        <SelectTrigger id={`reassignment-${key}`} aria-invalid={errors[key] ? true : undefined}>
          <SelectValue placeholder={t("dealStatus.reassignmentModal.select")} />
        </SelectTrigger>
        <SelectContent>
          {YES_NO.map((value) => (
            <SelectItem key={value} value={value}>
              {t(value === "Y" ? "dealStatus.reassignmentModal.yes" : "dealStatus.reassignmentModal.no")}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {errors[key] && (
        <p role="alert" className="text-destructive text-xs">
          {errors[key]}
        </p>
      )}
    </div>
  );

  const textField = (key: "dealerContactName" | "telephoneNumber" | "faxNumber", label: string, maxLength: number) => (
    <div className={fieldClassName}>
      <Label htmlFor={`reassignment-${key}`} className={labelClassName}>
        {label}
      </Label>
      <Input
        id={`reassignment-${key}`}
        value={form[key]}
        maxLength={maxLength}
        aria-invalid={errors[key] ? true : undefined}
        onChange={(event) => setField(key, event.target.value)}
      />
      {errors[key] && (
        <p role="alert" className="text-destructive text-xs">
          {errors[key]}
        </p>
      )}
    </div>
  );

  const history = details.notesHistory ?? [];
  const autopayActive = autopay === "Y";

  return (
    <>
      <div className="gap-(--spacing-3) p-(--spacing-4) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) max-h-[70vh] overflow-y-auto">
        <div className="gap-(--spacing-3) grid grid-cols-1 sm:grid-cols-2">
          {textField("dealerContactName", t("dealStatus.reassignmentModal.dealerContactName"), 100)}
          {textField("telephoneNumber", t("dealStatus.reassignmentModal.telephoneNumber"), 20)}
          {textField("faxNumber", t("dealStatus.reassignmentModal.faxNumber"), 20)}

          <div className={fieldClassName}>
            <Label htmlFor="reassignment-reasonId" className={labelClassName}>
              {t("dealStatus.reassignmentModal.reason")}
            </Label>
            <Select value={form.reasonId} onValueChange={(value) => setField("reasonId", value)}>
              <SelectTrigger id="reassignment-reasonId" aria-invalid={errors.reasonId ? true : undefined}>
                <SelectValue placeholder={t("dealStatus.reassignmentModal.select")} />
              </SelectTrigger>
              <SelectContent>
                {savedReasonInactive && savedReason?.id !== undefined && (
                  <SelectItem value={String(savedReason.id)} disabled>
                    {savedReason.name ?? savedReason.code}
                  </SelectItem>
                )}
                {reasons.map((reason) => (
                  <SelectItem key={reason.id} value={String(reason.id)}>
                    {reason.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.reasonId && (
              <p role="alert" className="text-destructive text-xs">
                {errors.reasonId}
              </p>
            )}
          </div>

          {yesNoField("approvedByOwnerOrGm", t("dealStatus.reassignmentModal.approvedByOwnerOrGm"))}
          {yesNoField(
            "reAssignmentComplete",
            t("dealStatus.reassignmentModal.reassignmentCompleted"),
            completedReadOnly
          )}
          {yesNoField("lienReleaseRequired", t("dealStatus.reassignmentModal.lienReleaseRequired"))}

          <div className={fieldClassName}>
            <span className={labelClassName}>{t("dealStatus.reassignmentModal.planType")}</span>
            <span className="text-sm">{planType || "—"}</span>
          </div>
          <div className={fieldClassName}>
            <span className={labelClassName}>{t("dealStatus.reassignmentModal.autopay")}</span>
            <span className="text-sm">{autopay}</span>
          </div>
        </div>

        {autopayActive && (
          <p role="note" className="text-muted-foreground mt-(--spacing-2) text-xs">
            {t("dealStatus.reassignmentModal.autopayNote")}
          </p>
        )}

        <div className={`${fieldClassName} mt-(--spacing-3)`}>
          <Label htmlFor="reassignment-notes" className={labelClassName}>
            {t("dealStatus.reassignmentModal.notes")}
          </Label>
          <Textarea
            id="reassignment-notes"
            value={form.notes}
            maxLength={2000}
            onChange={(event) => setField("notes", event.target.value)}
          />
        </div>

        <div className={`${fieldClassName} mt-(--spacing-3)`}>
          <span className={labelClassName}>{t("dealStatus.reassignmentModal.notesHistory")}</span>
          <div
            role="log"
            aria-label={t("dealStatus.reassignmentModal.notesHistory")}
            className="border-border p-(--spacing-2) bg-muted max-h-32 overflow-y-auto rounded-md border text-xs"
          >
            {history.length === 0 ? (
              <p className="text-muted-foreground">{t("dealStatus.reassignmentModal.noNotes")}</p>
            ) : (
              history.map((entry, index) => (
                <p key={`${entry.createdAt}-${index}`} className="mb-(--spacing-1)">
                  <span className="font-(--font-weight-semibold)">
                    {formatNoteDate(entry.createdAt)} {entry.createdByUserId}
                  </span>{" "}
                  {entry.notes}
                </p>
              ))
            )}
          </div>
        </div>

        <Button
          type="button"
          variant="link"
          size="sm"
          disabled={openingContract}
          onClick={() => void handleMailContract()}
          className="mt-(--spacing-2) h-auto p-0 text-xs underline"
        >
          {t("dealStatus.reassignmentModal.mailContract")}
        </Button>
      </div>

      {submitError && (
        <p role="alert" className="text-destructive px-(--spacing-4) pb-(--spacing-2) text-xs">
          {submitError}
        </p>
      )}

      <DialogFooter className="gap-(--spacing-2) px-(--spacing-4) py-(--spacing-2) justify-center border-t sm:justify-center">
        <Button type="button" size="xs" disabled={saveReassignment.isPending} onClick={() => void handleSave()}>
          {saveReassignment.isPending && <Loader2 aria-hidden="true" className="size-3 animate-spin" />}
          {t("dealStatus.reassignmentModal.save")}
        </Button>
        <DialogClose asChild>
          <Button type="button" size="xs" variant="outline" disabled={saveReassignment.isPending}>
            {t("dealStatus.reassignmentModal.cancel")}
          </Button>
        </DialogClose>
      </DialogFooter>
    </>
  );
}

// Opened from the "Reassignment Form" link in DealStatus (CAC users, status RAR/RAP/RAC only).
function ReassignmentModal({
  open,
  onOpenChange,
  approvalId,
  statusCode,
  planType,
  roleName,
  onSaved,
  onCancel,
}: ReassignmentModalProperties) {
  const { t } = useTranslation();
  const details = useReassignment(approvalId, open);
  const reasons = useReassignmentReasons(open);
  const prefill = useReassignmentPrefill(approvalId, open);

  const isLoading = details.isLoading || reasons.isLoading || prefill.isLoading;
  const error = details.error ?? reasons.error;
  const autopayFlag = prefill.data?.autopayAuthFlag?.toUpperCase();
  const autopay = autopayFlag === "Y" || autopayFlag === "N" ? autopayFlag : "—";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          onCancel?.();
        }
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="gap-(--spacing-0) p-(--spacing-0) overflow-hidden bg-white sm:max-w-2xl"
      >
        <div className="bg-primary text-primary-foreground px-(--spacing-4) py-(--spacing-2) flex items-center justify-between">
          <DialogTitle className="text-primary-foreground text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold)">
            {t("dealStatus.reassignmentModal.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("dealStatus.reassignmentModal.description")}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-(--spacing-0-5) transition-colors">
            <X className="size-4.5" aria-hidden="true" />
            <span className="sr-only">{t("dealStatus.reassignmentModal.close")}</span>
          </DialogClose>
        </div>

        {isLoading && (
          <div className="text-muted-foreground gap-(--spacing-1) p-(--spacing-6) flex items-center text-xs">
            <Loader2 aria-hidden="true" className="size-3 animate-spin" />
            {t("dealStatus.reassignmentModal.loading")}
          </div>
        )}

        {error && (
          <p role="alert" className="text-destructive p-(--spacing-6) text-xs">
            {t("dealStatus.reassignmentModal.loadError", { message: getErrorMessage(error) })}
          </p>
        )}

        {details.data && reasons.data && (
          <ReassignmentForm
            key={details.dataUpdatedAt}
            approvalId={approvalId}
            statusCode={statusCode}
            planType={planType}
            roleName={roleName}
            details={details.data}
            reasons={reasons.data}
            prefill={prefill.data}
            autopay={autopay}
            onSaved={async () => {
              onOpenChange(false);
              await onSaved?.();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReassignmentModal;
